import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Upload,
  Download,
  Users,
  Send,
  Loader2,
  Trash2,
  ArrowLeft,
  Plus,
  FileText,
  Sparkles,
  History,
  X,
  Bot,
  TrendingUp,
  Mail,
  Layers,
  Kanban,
  ChevronDown,
  Phone,
  Building2,
  Receipt,
  Calendar,
  User,
  Search,
  Repeat,
  StickyNote,
  Save,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import MediaUploader from "../components/media/MediaUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("sb_admin_token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Card = ({
  icon: Icon,
  color,
  title,
  children,
  testid,
  right,
  collapsible = false,
  defaultCollapsed = false,
  collapsedSummary,
}) => {
  // Persist collapse state per-testid in localStorage so the page reopens
  // exactly how the admin left it (works identically on mobile and desktop).
  const storageKey = collapsible && testid ? `sb_admin_card_open:${testid}` : null;
  const [collapsed, setCollapsed] = useState(() => {
    if (!collapsible) return false;
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved === "open") return false;
        if (saved === "closed") return true;
      } catch {
        /* SSR / storage unavailable — fall through to default */
      }
    }
    return defaultCollapsed;
  });
  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      const nextValue = next ? "closed" : "open";
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, nextValue);
        } catch {
          /* storage full or blocked — silently ignore, state still works this session */
        }
      }
      // Fire-and-forget cross-device sync. If backend is offline or the
      // admin is logged out, localStorage is still the source of truth.
      if (testid) {
        axios
          .patch(
            `${API}/admin/dashboard/prefs`,
            { card_states_update: { [testid]: nextValue } },
            { headers: authHeaders() },
          )
          .catch(() => {});
      }
      return next;
    });
  };

  // When backend prefs finish loading (async, after this card has
  // already mounted), re-read localStorage and reconcile our state
  // in case the value on another device differs.
  useEffect(() => {
    if (!storageKey) return;
    const reconcile = () => {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved === "open") setCollapsed(false);
        else if (saved === "closed") setCollapsed(true);
      } catch {
        /* storage unavailable */
      }
    };
    window.addEventListener("sb-card-states-synced", reconcile);
    return () => window.removeEventListener("sb-card-states-synced", reconcile);
  }, [storageKey]);
  return (
    <div
      className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5 sm:p-6 md:p-7"
      data-testid={testid}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={collapsible ? toggle : undefined}
          disabled={!collapsible}
          aria-expanded={collapsible ? !collapsed : undefined}
          data-testid={collapsible ? `${testid}-collapse-toggle` : undefined}
          className={`flex items-center gap-3 text-left min-w-0 ${
            collapsible ? "cursor-pointer group" : "cursor-default"
          }`}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border flex-shrink-0"
            style={{ background: `${color}18`, borderColor: `${color}45`, color }}
          >
            <Icon size={16} />
          </div>
          <div className="font-display font-bold text-lg tracking-tight truncate">
            {title}
          </div>
          {collapsible && (
            <ChevronDown
              size={16}
              className={`text-white/50 transition-transform group-hover:text-white flex-shrink-0 ${
                collapsed ? "-rotate-90" : ""
              }`}
            />
          )}
        </button>
        {right && (
          <div className="w-full sm:w-auto sm:flex-shrink-0 overflow-x-auto no-scrollbar">
            {right}
          </div>
        )}
      </div>
      {collapsible && collapsed ? (
        collapsedSummary ? (
          <button
            type="button"
            onClick={toggle}
            data-testid={`${testid}-expand-hint`}
            className="w-full text-left text-[12px] text-[#a395c3] hover:text-white transition py-2"
          >
            {collapsedSummary}
          </button>
        ) : null
      ) : (
        children
      )}
    </div>
  );
};

const Stat = ({ label, value, color = "#00f0ff" }) => (
  <div className="flex-1 min-w-[110px] rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-3">
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">{label}</div>
    <div className="text-2xl font-display font-black mt-1" style={{ color }}>
      {value}
    </div>
  </div>
);

const MiniStat = ({ icon: Icon, label, value, sub, color = "#00f0ff" }) => (
  <div className="rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-3">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={12} style={{ color }} />
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">{label}</div>
    </div>
    <div className="text-2xl font-display font-black" style={{ color }}>
      {value}
    </div>
    {sub && <div className="text-[10px] text-[#a395c3] mt-0.5">{sub}</div>}
  </div>
);

const AddClientForm = ({ onDone }) => {
  const [row, setRow] = useState({
    email: "",
    name: "",
    phone: "",
    business_name: "",
    invoice_number: "",
    invoice_date: "",
    invoice_amount: "",
    invoice_description: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/clients`,
        {
          ...row,
          invoice_amount: Number(row.invoice_amount || 0),
        },
        { headers: authHeaders() },
      );
      toast.success("Client saved.");
      setRow({
        email: "", name: "", phone: "", business_name: "",
        invoice_number: "", invoice_date: "", invoice_amount: "", invoice_description: "",
      });
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };
  const fields = [
    ["email", "Email *", "email"],
    ["name", "Full name", "text"],
    ["phone", "Phone", "text"],
    ["business_name", "Business name", "text"],
    ["invoice_number", "Invoice #", "text"],
    ["invoice_date", "Invoice date", "text"],
    ["invoice_amount", "Amount ($)", "number"],
    ["invoice_description", "Description", "text"],
  ];
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-marketing-add-client-form">
      {fields.map(([k, label, type]) => (
        <label key={k} className="text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">
          {label}
          <input
            type={type}
            required={k === "email"}
            value={row[k]}
            onChange={(e) => setRow({ ...row, [k]: e.target.value })}
            data-testid={`admin-add-client-${k}`}
            className="mt-1.5 w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-2.5 text-sm text-white normal-case tracking-normal"
          />
        </label>
      ))}
      <button
        type="submit"
        disabled={saving}
        data-testid="admin-add-client-submit"
        className="md:col-span-2 inline-flex items-center justify-center gap-2 mt-2 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition disabled:opacity-60"
      >
        {saving && <Loader2 size={14} className="animate-spin" />}
        <Plus size={14} /> Save client
      </button>
    </form>
  );
};

const CampaignComposer = ({ audience, onSent }) => {
  const [form, setForm] = useState({
    subject: "",
    html_body: "",
    segment: "paid_clients",
    from_name: "",
    scheduled_at: "",
  });
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [angle, setAngle] = useState("");

  const segCount =
    form.segment === "paid_clients"
      ? audience.paid_clients
      : form.segment === "guests"
        ? audience.guests
        : form.segment === "prospects"
          ? audience.prospects
          : form.segment === "reopen_waitlist"
            ? audience.reopen_waitlist || 0
            : audience.all_unique;

  const isScheduled = !!form.scheduled_at;

  const draftFromTop = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/campaigns/draft-from-top`,
        { segment: form.segment, angle: angle || "" },
        { headers: authHeaders() },
      );
      setForm((f) => ({ ...f, subject: data.subject, html_body: data.html_body }));
      if (data.source_subject) {
        toast.success(`Drafted from "${data.source_subject}" (score ${data.source_score})`);
      } else {
        toast.success("Cold-start draft ready — refine as needed. Future drafts will learn from your shipped campaigns.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draft failed — try again once you have a shipped campaign with real engagement.");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    setSending(true);
    setConfirmOpen(false);
    try {
      const payload = { ...form };
      // Convert datetime-local (browser local) → UTC ISO
      if (payload.scheduled_at) {
        payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      } else {
        delete payload.scheduled_at;
      }
      const { data } = await axios.post(`${API}/admin/campaigns`, payload, {
        headers: authHeaders(),
      });
      if (data.status === "scheduled") {
        toast.success(`Campaign scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
      } else {
        toast.success(`Campaign sent: ${data.sent} delivered · ${data.failed} failed`);
      }
      setForm({ subject: "", html_body: "", segment: "paid_clients", from_name: "", scheduled_at: "" });
      onSent && onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const [sendingTest, setSendingTest] = useState(false);
  const sendTestToMe = async () => {
    if (!form.subject.trim() || !form.html_body.trim()) {
      toast.error("Fill in subject and body before sending a test.");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/campaigns/send-test`,
        {
          subject: form.subject,
          html_body: form.html_body,
          from_name: form.from_name || undefined,
        },
        { headers: authHeaders() },
      );
      toast.success(`Test sent to ${data.to}. Check your inbox.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed.");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div data-testid="admin-marketing-composer">
      {/* AI draft-from-top-performer strip */}
      <div className="mb-5 flex flex-col md:flex-row gap-3 items-start md:items-center p-3 rounded-2xl border border-[#a78bfa]/30 bg-[#a78bfa]/[0.06]">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#a78bfa]/15 border border-[#a78bfa]/40 text-[#a78bfa] flex items-center justify-center">
            <Sparkles size={14} />
          </div>
          <div className="text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">Draft from top performer</div>
        </div>
        <input
          value={angle}
          onChange={(e) => setAngle(e.target.value)}
          placeholder="Optional angle (e.g. 'Valentine's week', 'reactivation nudge')"
          data-testid="admin-campaign-draft-angle"
          className="flex-1 rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#a78bfa]/50 outline-none px-3 py-2 text-sm text-white normal-case tracking-normal"
        />
        <button
          type="button"
          onClick={draftFromTop}
          disabled={drafting}
          data-testid="admin-campaign-draft-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a78bfa] text-[#05050a] font-semibold text-sm hover:bg-white transition disabled:opacity-60 flex-shrink-0"
        >
          {drafting && <Loader2 size={14} className="animate-spin" />}
          <Sparkles size={14} /> {drafting ? "Drafting…" : "Auto-draft"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">
          Subject *
          <input
            required
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="A message from SB Media Studio"
            data-testid="admin-campaign-subject"
            className="mt-1.5 w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-2.5 text-sm text-white normal-case tracking-normal"
          />
        </label>
        <label className="text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">
          Segment
          <select
            value={form.segment}
            onChange={(e) => setForm({ ...form, segment: e.target.value })}
            data-testid="admin-campaign-segment"
            className="mt-1.5 w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-2.5 text-sm text-white normal-case tracking-normal"
          >
            <option value="paid_clients">Paid clients ({audience.paid_clients})</option>
            <option value="guests">Spotlight guests ({audience.guests})</option>
            <option value="prospects">Prospects ({audience.prospects})</option>
            <option value="reopen_waitlist">Reopen waitlist ({audience.reopen_waitlist || 0})</option>
            <option value="all">All unique ({audience.all_unique})</option>
          </select>
        </label>
      </div>
      <label className="block mt-3 text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">
        HTML body *
        <textarea
          required
          rows={10}
          value={form.html_body}
          onChange={(e) => setForm({ ...form, html_body: e.target.value })}
          placeholder="<p>Hi,</p><p>...</p>"
          data-testid="admin-campaign-body"
          className="mt-1.5 w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-3 text-sm text-white font-mono normal-case tracking-normal resize-y"
        />
      </label>
      <label className="block mt-3 text-[11px] text-[#a395c3] uppercase tracking-[0.24em]">
        Schedule for later (optional — leave blank to send now)
        <input
          type="datetime-local"
          value={form.scheduled_at}
          onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
          data-testid="admin-campaign-schedule"
          className="mt-1.5 w-full md:w-72 rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-2.5 text-sm text-white normal-case tracking-normal"
        />
      </label>
      <p className="text-[11px] text-[#a395c3]/70 mt-2">
        Plain HTML supported. Use `&lt;p&gt;`, `&lt;a href&gt;`, `&lt;strong&gt;`, `&lt;br&gt;`. Sender identity comes from RESEND_SENDER_EMAIL.
      </p>
      <button
        type="button"
        disabled={sending || !form.subject || !form.html_body || segCount === 0}
        onClick={() => setConfirmOpen(true)}
        data-testid="admin-campaign-send"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] transition disabled:opacity-40"
      >
        <Send size={14} />
        {isScheduled
          ? `Schedule for ${segCount} recipient${segCount === 1 ? "" : "s"}`
          : `Send to ${segCount} recipient${segCount === 1 ? "" : "s"}`}
      </button>

      {confirmOpen && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" data-testid="admin-campaign-confirm">
          <div className="max-w-md w-full rounded-3xl border border-white/10 bg-[#0d0716] p-7">
            <div className="w-12 h-12 rounded-2xl bg-[#ff0055]/15 border border-[#ff0055]/40 text-[#ff0055] flex items-center justify-center mb-4">
              <Send size={20} />
            </div>
            <h3 className="font-display font-black text-2xl tracking-tight">
              {isScheduled ? "Schedule this campaign?" : "Send this campaign?"}
            </h3>
            <p className="text-[#a395c3] text-sm mt-2">
              {isScheduled
                ? <>Queued to send at <span className="text-white font-semibold">{new Date(form.scheduled_at).toLocaleString()}</span> to <span className="text-white font-semibold">{segCount}</span> {form.segment.replace("_", " ")}.</>
                : <>This will email <span className="text-white font-semibold">{segCount}</span> {form.segment.replace("_", " ")} immediately.</>}
              {" "}Subject: &ldquo;<span className="text-white">{form.subject}</span>&rdquo;.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmOpen(false)}
                data-testid="admin-campaign-cancel"
                className="flex-1 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={send}
                data-testid="admin-campaign-confirm-send"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] transition"
              >
                <Send size={14} /> {isScheduled ? "Schedule now" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
/**
 * RosterFilterBar — search + filter pills for the Client Roster.
 * Search hits email / name / business / phone / notes.
 * Filters: all, paid (has ≥1 paid invoice), unpaid (no paid invoice),
 * retainer (has_retainer flag from backend, cross-referenced with
 * active Stripe subscriptions).
 */
const RosterFilterBar = ({ search, onSearch, filter, onFilter, counts }) => {
  const pills = [
    ["all", "All", counts.all, "#00f0ff"],
    ["paid", "Paid", counts.paid, "#22c55e"],
    ["unpaid", "Unpaid", counts.unpaid, "#f5c065"],
    ["retainer", "Retainer", counts.retainer, "#a78bfa"],
  ];
  return (
    <div className="mb-4 space-y-3" data-testid="roster-filter-bar">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, email, business, phone, notes…"
          data-testid="roster-search-input"
          className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none text-sm text-white normal-case tracking-normal"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearch("")}
            data-testid="roster-search-clear"
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10"
          >
            <X size={12} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {pills.map(([key, label, count, color]) => {
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onFilter(key)}
              data-testid={`roster-filter-${key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap transition border ${
                active
                  ? "text-[#05050a]"
                  : "text-white/70 bg-white/[0.04] border-white/10 hover:bg-white/[0.08]"
              }`}
              style={
                active
                  ? { background: color, borderColor: color }
                  : undefined
              }
            >
              {label}
              <span
                className={`text-[10px] font-mono ${active ? "opacity-70" : "text-white/45"}`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * ClientRosterRow — collapsed "pill" for a paid client that expands
 * inline to show the full backend profile (contact + invoices + totals).
 * Replaces the flat table view — much easier to scan and drill in on
 * mobile, and doesn't require a navigation away from the roster.
 */
const ClientRosterRow = ({ client, onDelete, onNotesSaved }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("profile"); // profile | notes | media
  const [notes, setNotes] = useState(client.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const invoices = client.invoices || [];
  const total = invoices.reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
  const paidCount = invoices.filter(
    (inv) => (inv.status || "").toLowerCase() === "paid",
  ).length;
  const lastInvoice = invoices[0];
  const hasRetainer = !!client.has_retainer;

  // Keep local notes in sync when the roster reloads
  useEffect(() => {
    setNotes(client.notes || "");
    setNotesDirty(false);
  }, [client.email, client.notes]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(
        `${API}/admin/clients/${encodeURIComponent(client.email)}/notes`,
        { notes },
        { headers: authHeaders() },
      );
      toast.success("Notes saved.");
      setNotesDirty(false);
      onNotesSaved && onNotesSaved(client.email, notes);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save notes.");
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div
      data-testid={`client-roster-${client.email}`}
      className={`rounded-2xl border transition-colors ${
        open
          ? "border-[#00f0ff]/40 bg-[#00f0ff]/[0.04]"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`client-roster-toggle-${client.email}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/10 border border-[#00f0ff]/30 text-[#00f0ff] flex items-center justify-center flex-shrink-0 font-display font-black text-sm">
          {(client.name || client.email || "?").trim().charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate max-w-[180px] sm:max-w-none">
              {client.name || client.email}
            </span>
            {client.business_name && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#a395c3] truncate">
                · {client.business_name}
              </span>
            )}
            {hasRetainer && (
              <span
                data-testid={`client-retainer-badge-${client.email}`}
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-full bg-[#a78bfa]/15 border border-[#a78bfa]/45 text-[#a78bfa]"
              >
                <Repeat size={9} /> Retainer
              </span>
            )}
            {(client.notes || "").trim().length > 0 && (
              <span
                title="Has notes"
                className="inline-flex items-center text-[#f5c065]"
              >
                <StickyNote size={11} />
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/50 mt-0.5 truncate">
            {client.email}
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end flex-shrink-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">
            {invoices.length} inv · {paidCount} paid
          </div>
          <div className="text-sm font-mono font-semibold text-white mt-0.5">
            ${total.toFixed(2)}
          </div>
        </div>
        <ChevronDown
          size={16}
          className={`text-white/50 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="px-4 pb-4 pt-1 border-t border-white/[0.08] space-y-4"
          data-testid={`client-roster-profile-${client.email}`}
        >
          <div className="sm:hidden flex items-center justify-between gap-2 pt-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">
              {invoices.length} invoice{invoices.length === 1 ? "" : "s"} · {paidCount} paid
            </div>
            <div className="text-lg font-mono font-black text-[#00f0ff]">
              ${total.toFixed(2)}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 pt-2 -mx-1 overflow-x-auto no-scrollbar">
            {[
              ["profile", "Profile", User],
              ["notes", "Notes", StickyNote],
              ["media", "Media", ImageIcon],
            ].map(([key, label, Icon]) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`client-tab-${key}-${client.email}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap transition ${
                    active
                      ? "bg-[#00f0ff]/15 border border-[#00f0ff]/45 text-[#00f0ff]"
                      : "bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/[0.08]"
                  }`}
                >
                  <Icon size={11} /> {label}
                </button>
              );
            })}
          </div>

          {tab === "profile" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <ProfileField icon={Mail} label="Email" value={client.email} copyable />
                <ProfileField icon={Phone} label="Phone" value={client.phone} copyable />
                <ProfileField icon={Building2} label="Business" value={client.business_name} />
                <ProfileField icon={User} label="Source" value={client.source || "manual"} />
                {lastInvoice?.date && (
                  <ProfileField
                    icon={Calendar}
                    label="Last invoice"
                    value={new Date(lastInvoice.date).toLocaleDateString()}
                  />
                )}
                {hasRetainer && (
                  <ProfileField icon={Repeat} label="Retainer" value="Active" />
                )}
              </div>

              {invoices.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2 flex items-center gap-1.5">
                    <Receipt size={11} /> Invoice history
                  </div>
                  <div className="rounded-xl bg-black/25 border border-white/10 divide-y divide-white/[0.06] max-h-[280px] overflow-y-auto">
                    {invoices.map((inv, i) => {
                      const paid = (inv.status || "").toLowerCase() === "paid";
                      return (
                        <div
                          key={`${inv.number || i}-${i}`}
                          data-testid={`client-invoice-${client.email}-${i}`}
                          className="flex items-center gap-3 px-3 py-2.5"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-mono-tight text-[12px] text-white truncate">
                              {inv.number || `Invoice ${i + 1}`}
                            </div>
                            <div className="text-[10px] text-white/40 mt-0.5 truncate">
                              {inv.date ? new Date(inv.date).toLocaleDateString() : "—"}
                              {inv.description && ` · ${inv.description}`}
                            </div>
                          </div>
                          <span
                            className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${
                              paid
                                ? "bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/40"
                                : "bg-white/5 text-white/60 border-white/20"
                            }`}
                          >
                            {inv.status || "sent"}
                          </span>
                          <div className="font-mono text-sm text-white flex-shrink-0">
                            ${Number(inv.amount || 0).toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "notes" && (
            <div data-testid={`client-notes-panel-${client.email}`}>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2 flex items-center gap-1.5">
                <StickyNote size={11} /> Internal notes
              </div>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setNotesDirty(true);
                }}
                rows={5}
                maxLength={4000}
                placeholder="Referral source, delivery quirks, VIP alerts, private context…"
                data-testid={`client-notes-input-${client.email}`}
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-3 py-2.5 text-sm text-white normal-case tracking-normal resize-y"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-[10px] text-white/40">
                  {notes.length}/4000 characters
                </div>
                <button
                  type="button"
                  onClick={saveNotes}
                  disabled={savingNotes || !notesDirty}
                  data-testid={`client-notes-save-${client.email}`}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-xs hover:bg-white transition disabled:opacity-40"
                >
                  {savingNotes ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {savingNotes ? "Saving…" : notesDirty ? "Save notes" : "Saved"}
                </button>
              </div>
            </div>
          )}

          {tab === "media" && (
            <div data-testid={`client-media-panel-${client.email}`}>
              <MediaUploader
                ownerType="client"
                ownerId={client.email}
                authMode="admin"
                compact
                title="Client media"
                emptyLabel="No media attached to this client yet — drop a file to add one."
                testIdPrefix={`client-media-${client.email}`}
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <a
              href={`mailto:${client.email}`}
              data-testid={`client-mailto-${client.email}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-xs hover:bg-white/[0.1]"
            >
              <Mail size={12} /> Email
            </a>
            <button
              type="button"
              onClick={onDelete}
              data-testid={`client-delete-${client.email}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ef4444]/10 border border-[#ef4444]/40 text-[#ef4444] text-xs font-semibold hover:bg-[#ef4444]/20"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileField = ({ icon: Icon, label, value, copyable = false }) => {
  if (!value) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success("Copied."); }
    catch { /* clipboard blocked — ignore */ }
  };
  return (
    <div
      className="rounded-xl bg-white/[0.02] border border-white/10 px-3 py-2"
      data-testid={`client-field-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.24em] text-[#a395c3]">
        <Icon size={10} /> {label}
      </div>
      <button
        type="button"
        onClick={copyable ? copy : undefined}
        className={`text-sm text-white mt-0.5 truncate w-full text-left ${copyable ? "hover:text-[#00f0ff]" : "cursor-default"}`}
      >
        {value}
      </button>
    </div>
  );
};


const AdminMarketing = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [audience, setAudience] = useState({ paid_clients: 0, guests: 0, prospects: 0, reopen_waitlist: 0, all_unique: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [exportSegment, setExportSegment] = useState("all");
  const [rosterSearch, setRosterSearch] = useState("");
  const [rosterFilter, setRosterFilter] = useState("all"); // all | paid | unpaid | retainer

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const [aud, cli, cam, anly] = await Promise.all([
        axios.get(`${API}/admin/marketing/audience`, { headers }),
        axios.get(`${API}/admin/clients`, { headers }),
        axios.get(`${API}/admin/campaigns`, { headers }),
        axios.get(`${API}/admin/sable/analytics`, { headers }),
      ]);
      setAudience(aud.data);
      setClients(cli.data.items || []);
      setCampaigns(cam.data.items || []);
      setAnalytics(anly.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to load marketing data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // Sync collapse state from backend so the accordion opens
    // exactly how the admin left it on any other device. Cards
    // read from localStorage on first render, so seed it before
    // that happens on the *next* mount — the visible flash on a
    // fresh device is a single re-render at most.
    axios
      .get(`${API}/admin/dashboard/prefs`, { headers: authHeaders() })
      .then(({ data }) => {
        const states = data.card_states || {};
        let touched = false;
        Object.entries(states).forEach(([key, value]) => {
          if (value !== "open" && value !== "closed") return;
          try {
            const storageKey = `sb_admin_card_open:${key}`;
            if (localStorage.getItem(storageKey) !== value) {
              localStorage.setItem(storageKey, value);
              touched = true;
            }
          } catch {
            /* storage unavailable */
          }
        });
        // Signal any mounted Card components that their persisted
        // state may have changed under them.
        if (touched && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("sb-card-states-synced"));
        }
      })
      .catch(() => { /* offline or 401 — localStorage still works */ });
  }, []);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/admin/clients/csv-upload`, fd, {
        headers: { ...authHeaders() },
      });
      toast.success(`Uploaded ${data.processed} rows · ${data.created} new · ${data.updated} updated`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadExport = () => {
    const token = localStorage.getItem("sb_admin_token");
    // Fetch as blob with auth header, then trigger download
    axios
      .get(`${API}/admin/marketing/export`, {
        params: { segment: exportSegment },
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      })
      .then((r) => {
        const blob = new Blob([r.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `sbmedia_marketing_${exportSegment}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Export downloaded.");
      })
      .catch(() => toast.error("Export failed."));
  };

  const removeClient = async (email) => {
    if (!window.confirm(`Remove ${email}? This won't affect their bookings or spotlight records.`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${encodeURIComponent(email)}`, {
        headers: authHeaders(),
      });
      toast.success("Client removed.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };

  const cancelCampaign = async (id) => {
    if (!window.confirm("Cancel this scheduled campaign? Recipients will NOT be emailed.")) return;
    try {
      await axios.post(`${API}/admin/campaigns/${id}/cancel`, {}, { headers: authHeaders() });
      toast.success("Campaign cancelled.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#05050a] text-white" data-testid="admin-marketing-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#a395c3] hover:text-white transition mb-3"
              data-testid="admin-marketing-back"
            >
              <ArrowLeft size={12} /> Hub
            </button>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              Marketing <span className="text-[#00f0ff] text-glow-cyan">& CRM</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2 max-w-xl">
              Upload paid-client rosters, export segmented marketing lists, send email blasts via Resend, and manage every Kanban pipeline.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/admin/boards"
                data-testid="marketing-boards-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#a78bfa]/15 border border-[#a78bfa]/45 text-[#a78bfa] text-[11px] font-semibold hover:bg-[#a78bfa] hover:text-[#05050a] transition"
              >
                <Layers size={11} /> Kanban boards
              </Link>
              <Link
                to="/admin/pipeline"
                data-testid="marketing-pipeline-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00f0ff]/15 border border-[#00f0ff]/45 text-[#00f0ff] text-[11px] font-semibold hover:bg-[#00f0ff] hover:text-[#05050a] transition"
              >
                <Kanban size={11} /> Studio pipeline
              </Link>
              <Link
                to="/admin/analytics/reports"
                data-testid="marketing-reports-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-white/80 text-[11px] font-semibold hover:bg-white/[0.1] transition"
              >
                <FileText size={11} /> Weekly reports
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-[#00f0ff]" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Audience stats */}
            <div className="flex flex-wrap gap-3">
              <Stat label="Paid clients" value={audience.paid_clients} color="#00f0ff" />
              <Stat label="Spotlight guests" value={audience.guests} color="#ff0055" />
              <Stat label="Prospects" value={audience.prospects} color="#a78bfa" />
              <Stat label="Reopen waitlist" value={audience.reopen_waitlist || 0} color="#f5c065" />
              <Stat label="All unique" value={audience.all_unique} color="#22c55e" />
            </div>

            {/* Sable analytics */}
            {analytics && (
              <Card
                icon={Bot}
                color="#00f0ff"
                title="Sable AI — chat → booking funnel"
                testid="admin-sable-analytics"
                collapsible
                defaultCollapsed
                collapsedSummary={`${analytics.total_chats} chats · ${Math.round(analytics.close_rate * 100)}% close rate — tap to view funnel.`}
                right={
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                    Last {analytics.window_days} days
                  </span>
                }
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <MiniStat icon={Mail} label="Total chats" value={analytics.total_chats} color="#00f0ff" />
                  <MiniStat icon={Users} label="Left contact" value={analytics.chats_with_email} sub={`${Math.round(analytics.contact_capture_rate * 100)}% capture`} color="#a78bfa" />
                  <MiniStat icon={TrendingUp} label="Booked after chat" value={analytics.bookings_from_chat} sub={`${Math.round(analytics.close_rate * 100)}% close`} color="#22c55e" />
                  <MiniStat icon={Send} label="Escalated to human" value={analytics.escalations} color="#ff0055" />
                </div>
                {analytics.daily.length > 0 && (
                  <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-4 overflow-x-auto">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3] mb-3">
                      Daily chats · last {analytics.daily.length} days
                    </div>
                    <div className="flex items-end gap-1 h-16">
                      {analytics.daily.map((d) => {
                        const max = Math.max(...analytics.daily.map((x) => x.chats), 1);
                        const h = Math.max(4, (d.chats / max) * 100);
                        const emailH = Math.max(0, (d.with_email / max) * 100);
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                            <div className="relative w-full flex flex-col justify-end h-16">
                              <div
                                className="w-full bg-[#00f0ff]/25 rounded-sm"
                                style={{ height: `${h}%` }}
                              />
                              <div
                                className="absolute bottom-0 w-full bg-[#22c55e] rounded-sm"
                                style={{ height: `${emailH}%` }}
                              />
                            </div>
                            <div className="text-[8px] text-[#a395c3] rotate-45 origin-top-left mt-1 opacity-70 group-hover:opacity-100">
                              {d.date.slice(5)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-[10px] text-[#a395c3]">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-[#00f0ff]/40 rounded-sm" /> Total chats
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-[#22c55e] rounded-sm" /> With captured email
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-[#a395c3]/70 mt-3 leading-relaxed">
                  Sable&apos;s system prompt is now enriched with the top-performing campaign copy on every chat turn (segment-detected by keywords). As more campaigns ship and get real opens/clicks, her conversion scripts get sharper.
                </div>
              </Card>
            )}

            {/* Roster / CSV upload */}
            <Card
              icon={Users}
              color="#00f0ff"
              title={`Client roster${clients.length ? ` · ${clients.length}` : ""}`}
              testid="admin-marketing-roster"
              collapsible
              collapsedSummary={
                clients.length
                  ? `${clients.length} client${clients.length === 1 ? "" : "s"} hidden — tap to expand.`
                  : "No clients yet — tap to expand."
              }
              right={
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`${API}/admin/clients/csv-template`}
                    download
                    onClick={(e) => {
                      // Add auth header via fetch since <a download> doesn't send it
                      e.preventDefault();
                      const token = localStorage.getItem("sb_admin_token");
                      axios
                        .get(`${API}/admin/clients/csv-template`, {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: "blob",
                        })
                        .then((r) => {
                          const blob = new Blob([r.data], { type: "text/csv" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "sbmedia_clients_template.csv";
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        })
                        .catch(() => toast.error("Template download failed."));
                    }}
                    data-testid="admin-clients-template"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/15 text-sm text-white/85 hover:bg-white/[0.08] transition"
                  >
                    <FileText size={14} /> Template
                  </a>
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/15 text-sm cursor-pointer hover:bg-white/[0.08] transition">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".csv,text/csv,text/comma-separated-values,application/csv,application/vnd.ms-excel,text/plain"
                      onChange={handleUpload}
                      className="sr-only"
                      data-testid="admin-clients-upload-input"
                    />
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Upload CSV
                  </label>
                  <button
                    onClick={() => setShowAdd((v) => !v)}
                    data-testid="admin-clients-add-toggle"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition"
                  >
                    <Plus size={14} /> {showAdd ? "Hide form" : "Add client"}
                  </button>
                </div>
              }
            >
              <div className="text-[11px] text-[#a395c3] mb-4">
                CSV columns:{" "}
                <code className="text-white bg-white/[0.06] px-1.5 py-0.5 rounded">
                  email, name, phone, business_name, invoice_number, invoice_date, invoice_amount, invoice_description
                </code>
                {" "}· one row per invoice; multiple rows with the same email will merge under one client.
              </div>

              {showAdd && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4">
                  <AddClientForm
                    onDone={() => {
                      setShowAdd(false);
                      loadAll();
                    }}
                  />
                </div>
              )}

              {clients.length === 0 ? (
                <div className="text-sm text-[#a395c3] italic py-4">
                  No paid clients yet. Upload a CSV or add one manually.
                </div>
              ) : (
                <>
                  <RosterFilterBar
                    search={rosterSearch}
                    onSearch={setRosterSearch}
                    filter={rosterFilter}
                    onFilter={setRosterFilter}
                    counts={{
                      all: clients.length,
                      paid: clients.filter((c) =>
                        (c.invoices || []).some((i) => (i.status || "").toLowerCase() === "paid"),
                      ).length,
                      unpaid: clients.filter(
                        (c) =>
                          (c.invoices || []).length === 0 ||
                          !(c.invoices || []).some((i) => (i.status || "").toLowerCase() === "paid"),
                      ).length,
                      retainer: clients.filter((c) => c.has_retainer).length,
                    }}
                  />
                  {(() => {
                    const q = rosterSearch.trim().toLowerCase();
                    const filtered = clients.filter((c) => {
                      // Filter pill
                      const invs = c.invoices || [];
                      const anyPaid = invs.some(
                        (i) => (i.status || "").toLowerCase() === "paid",
                      );
                      if (rosterFilter === "paid" && !anyPaid) return false;
                      if (rosterFilter === "unpaid" && anyPaid) return false;
                      if (rosterFilter === "retainer" && !c.has_retainer) return false;
                      // Search
                      if (!q) return true;
                      const hay = [
                        c.email,
                        c.name,
                        c.business_name,
                        c.phone,
                        c.notes,
                      ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();
                      return hay.includes(q);
                    });
                    if (filtered.length === 0) {
                      return (
                        <div
                          data-testid="client-roster-empty-filter"
                          className="text-sm text-[#a395c3] italic py-6 text-center"
                        >
                          No clients match your search or filter.
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2" data-testid="admin-clients-roster-list">
                        {filtered.map((c) => (
                          <ClientRosterRow
                            key={c.email}
                            client={c}
                            onDelete={() => removeClient(c.email)}
                            onNotesSaved={(email, notes) =>
                              setClients((prev) =>
                                prev.map((row) =>
                                  row.email === email ? { ...row, notes } : row,
                                ),
                              )
                            }
                          />
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </Card>

            {/* Marketing export */}
            <Card
              icon={Download}
              color="#22c55e"
              title="Export marketing CSV"
              testid="admin-marketing-export"
              collapsible
              defaultCollapsed
              collapsedSummary={`Deduped list of ${audience.all_unique} contacts — tap to export.`}
            >
              <div className="text-[13px] text-[#a395c3] mb-4">
                Deduplicated across sources. Columns: <code className="text-white bg-white/[0.06] px-1.5 py-0.5 rounded">email, name, phone, sources</code>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={exportSegment}
                  onChange={(e) => setExportSegment(e.target.value)}
                  data-testid="admin-export-segment"
                  className="rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#22c55e]/50 outline-none px-4 py-2.5 text-sm text-white"
                >
                  <option value="all">All ({audience.all_unique})</option>
                  <option value="paid_clients">Paid clients ({audience.paid_clients})</option>
                  <option value="guests">Spotlight guests ({audience.guests})</option>
                  <option value="prospects">Prospects ({audience.prospects})</option>
                </select>
                <button
                  onClick={downloadExport}
                  data-testid="admin-export-download"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22c55e] text-[#05050a] font-semibold text-sm hover:bg-white transition"
                >
                  <Download size={14} /> Download CSV
                </button>
              </div>
            </Card>

            {/* Campaign composer */}
            <Card
              icon={Sparkles}
              color="#ff0055"
              title="Compose email campaign"
              testid="admin-marketing-campaign"
              collapsible
              defaultCollapsed
              collapsedSummary="Draft, schedule, or fire a new email blast — tap to open."
            >
              <CampaignComposer audience={audience} onSent={loadAll} />
            </Card>

            {/* Campaign history */}
            <Card
              icon={History}
              color="#a78bfa"
              title={`Campaign history${campaigns.length ? ` · ${campaigns.length}` : ""}`}
              testid="admin-marketing-history"
              collapsible
              defaultCollapsed
              collapsedSummary={
                campaigns.length
                  ? `${campaigns.length} campaign${campaigns.length === 1 ? "" : "s"} — tap to view.`
                  : "No campaigns sent yet — tap to expand."
              }
            >
              {campaigns.length === 0 ? (
                <div className="text-sm text-[#a395c3] italic py-4">No campaigns sent yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.24em] text-[#a395c3] border-b border-white/10">
                        <th className="py-2 pr-3">Subject</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Segment</th>
                        <th className="py-2 pr-3">Sent</th>
                        <th className="py-2 pr-3">Failed</th>
                        <th className="py-2 pr-3">When</th>
                        <th className="py-2 pr-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c) => {
                        const status = c.status || (c.sent > 0 ? "sent" : "unknown");
                        const statusColor =
                          status === "scheduled"
                            ? "#a78bfa"
                            : status === "sent"
                              ? "#22c55e"
                              : status === "failed"
                                ? "#ff0055"
                                : status === "cancelled"
                                  ? "#a395c3"
                                  : "#00f0ff";
                        const when = c.status === "scheduled"
                          ? new Date(c.scheduled_at).toLocaleString() + " (scheduled)"
                          : c.completed_at
                            ? new Date(c.completed_at).toLocaleString()
                            : new Date(c.created_at).toLocaleString();
                        return (
                          <tr
                            key={c.id}
                            data-testid={`admin-campaign-row-${c.id}`}
                            className="border-b border-white/[0.06]"
                          >
                            <td className="py-2.5 pr-3 text-white truncate max-w-[280px]">{c.subject}</td>
                            <td className="py-2.5 pr-3">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold border"
                                style={{ background: `${statusColor}22`, borderColor: `${statusColor}55`, color: statusColor }}
                              >
                                {status}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-white/85">{c.segment.replace("_", " ")}</td>
                            <td className="py-2.5 pr-3 text-[#22c55e] font-mono">{c.sent}</td>
                            <td className="py-2.5 pr-3 text-[#ff0055] font-mono">{c.failed}</td>
                            <td className="py-2.5 pr-3 text-white/60 font-mono text-[12px]">{when}</td>
                            <td className="py-2.5 pr-3 text-right">
                              {status === "scheduled" && (
                                <button
                                  onClick={() => cancelCampaign(c.id)}
                                  data-testid={`admin-campaign-cancel-${c.id}`}
                                  className="text-[11px] text-[#a395c3] hover:text-[#ff0055] transition uppercase tracking-[0.2em]"
                                >
                                  Cancel
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketing;
